import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Loader2, Plus, Star, Trash2 } from 'lucide-react';
import {
  ConfirmDialog,
  EmptyState,
  FileDropZone,
  Modal,
  RateLimitInline,
  RESUME_ACCEPT_ATTR,
  Skeleton,
  formatBytes,
  formatDate,
  useToast,
  validateResumeFile,
} from '@qh/shared';
import {
  ApiError,
  RateLimitError,
  candidateApi,
  type ResumeListItem,
} from '../../lib/apiClient';

/**
 * My Resumes (analysis §5.4) — list, add (no AI parse — server explicitly skips),
 * mark primary, delete (with the in-use / last-resume guards). Download opens a
 * server-signed URL in a new tab.
 *
 * StrictMode-safe mount-time fetch via cleanup-cancellation flag (CLAUDE.md
 * Frontend Conventions).
 */
export function MyResumesPage() {
  const toast = useToast();
  const [resumes, setResumes] = useState<ResumeListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ResumeListItem | null>(null);
  const [actionPendingId, setActionPendingId] = useState<string | null>(null);
  const fetchedOnceRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await candidateApi.resumes.list();
      setResumes(rows);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load your resumes.');
    }
  }, []);

  useEffect(() => {
    if (fetchedOnceRef.current) return;
    let cancelled = false;
    candidateApi.resumes
      .list()
      .then((rows) => {
        if (cancelled) return;
        fetchedOnceRef.current = true;
        setResumes(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load your resumes.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMarkPrimary = useCallback(
    async (resume: ResumeListItem) => {
      if (resume.is_primary) return;
      setActionPendingId(resume.id);
      try {
        await candidateApi.resumes.markPrimary(resume.id);
        await refresh();
        toast.success('Primary resume updated.');
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not update primary resume.');
      } finally {
        setActionPendingId(null);
      }
    },
    [refresh, toast],
  );

  const handleDownload = useCallback(
    async (resume: ResumeListItem) => {
      setActionPendingId(resume.id);
      try {
        const { url, filename } = await candidateApi.resumes.downloadUrl(resume.id);
        // Backend signs the URL with `Content-Disposition: attachment` (local-disk
        // sets the header, Azure encodes it in the SAS via `rscd`). We click a hidden
        // anchor with `download` so the browser triggers a save instead of opening
        // a new tab when the user activated the action.
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not generate a download link.');
      } finally {
        setActionPendingId(null);
      }
    },
    [toast],
  );

  const handleDelete = useCallback(async () => {
    const target = confirmDelete;
    if (!target) return;
    setActionPendingId(target.id);
    try {
      await candidateApi.resumes.remove(target.id);
      setConfirmDelete(null);
      await refresh();
      toast.success('Resume deleted.');
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error('Could not delete the resume.');
      }
    } finally {
      setActionPendingId(null);
    }
  }, [confirmDelete, refresh, toast]);

  return (
    <section>
      <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
            My Resumes
          </h1>
          <p className="text-sm text-brand-muted mt-1">
            Upload up to multiple PDF/DOCX resumes (max 5 MB each). One resume must be marked Primary.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep shadow-sm transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add resume
        </button>
      </header>

      {loadError && (
        <div className="bg-white rounded-3xl border border-red-100 shadow-sm p-6 text-center">
          <p className="text-sm font-bold text-red-700 mb-3">{loadError}</p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:text-brand-deep"
          >
            Retry
          </button>
        </div>
      )}

      {!loadError && !resumes && (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-3xl" />
          ))}
        </div>
      )}

      {!loadError && resumes && resumes.length === 0 && (
        <EmptyState
          icon={<FileText className="w-8 h-8 text-brand-muted" />}
          title="No resumes yet"
          description="Upload your first resume so companies can review your application materials."
          action={
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep shadow-sm transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add resume
            </button>
          }
        />
      )}

      {!loadError && resumes && resumes.length > 0 && (
        <ul className="space-y-3">
          {resumes.map((resume) => (
            <li
              key={resume.id}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 flex flex-wrap items-center gap-4"
            >
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-brand-secondary flex items-center justify-center text-brand-deep">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-brand-primary truncate" title={resume.original_filename}>
                    {resume.original_filename}
                  </p>
                  {resume.is_primary && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-accent/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.18em] text-brand-deep border border-brand-accent/30">
                      <Star className="w-3 h-3 fill-brand-accent text-brand-accent" />
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-xs text-brand-muted mt-0.5">
                  {resume.file_type} · {formatBytes(resume.file_size_bytes)} · Uploaded {formatDate(resume.uploaded_at)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!resume.is_primary && (
                  <button
                    type="button"
                    onClick={() => void handleMarkPrimary(resume)}
                    disabled={actionPendingId === resume.id}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
                  >
                    <Star className="w-3 h-3" />
                    Mark primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void handleDownload(resume)}
                  disabled={actionPendingId === resume.id}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(resume)}
                  disabled={actionPendingId === resume.id}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-100 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AddResumeModal
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={() => {
          setAdding(false);
          void refresh();
          toast.success('Resume uploaded.');
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this resume?"
        description={
          confirmDelete
            ? `${confirmDelete.original_filename} will be removed. If it's attached to an active application or it's your only resume, the server will refuse the delete.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={actionPendingId === confirmDelete?.id}
        onConfirm={() => void handleDelete()}
        onClose={() => setConfirmDelete(null)}
      />
    </section>
  );
}

interface AddResumeModalProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

function AddResumeModal({ open, onClose, onAdded }: AddResumeModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [externalError, setExternalError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  useEffect(() => {
    if (open) return;
    setFile(null);
    setExternalError(null);
    setUploading(false);
    setRateLimitedUntil(null);
  }, [open]);

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setExternalError(null);
    try {
      await candidateApi.resumes.add(file);
      onAdded();
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        setExternalError(err.message);
      } else {
        setExternalError('Could not upload the resume. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!uploading) onClose();
      }}
      title="Add a resume"
      description="PDF or DOCX, max 5 MB. Adding a new resume does NOT auto-update your profile — use Re-parse from My Profile if you want that."
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="inline-flex items-center rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-muted hover:text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={
              !file ||
              uploading ||
              (rateLimitedUntil !== null && rateLimitedUntil > Date.now())
            }
            className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
          >
            {uploading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Uploading…
              </>
            ) : (
              'Upload'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <FileDropZone
          file={file}
          onSelect={(f) => {
            setFile(f);
            setExternalError(null);
          }}
          validate={validateResumeFile}
          accept={RESUME_ACCEPT_ATTR}
          label="Drop your resume here, or click to browse"
          hint="PDF or DOCX, max 5 MB"
          disabled={uploading}
          externalError={externalError}
        />
        <RateLimitInline deadlineMs={rateLimitedUntil} />
      </div>
    </Modal>
  );
}
